import { useState } from "react";
import { BackButton } from "../../components/BackButton";

const sections = [
  {
    title: "1. About Prep AI",
    content: [
      "Prep AI is an AI-driven educational platform designed to assist students preparing for:",
      "• WAEC, JAMB, NECO, and related examinations through personalized study plans.\n• AI tutoring and step-by-step explanations for complex topics.\n• Past questions and mock exams with instant feedback.\n• Diagnostic assessments and real-time performance tracking.",
      "PrepAI is an educational support tool and does not replace formal schooling or guarantee examination success.",
    ],
  },
  {
    title: "2. Eligibility",
    content: [
      "You may use Prep AI if:",
      "• You provide accurate registration details.\n• You register using your valid NIN or other valid means of identification (where required).\n• You are legally capable of entering into a binding agreement.",
      "Users Under 18 (Minors)\nIf you are under 18 years old:\n• You must obtain verified consent from your parent or legal guardian.\n• Your parent/guardian must approve your registration.\n• Your parent/guardian accepts responsibility for your use of the Platform.",
      "We reserve the right to suspend accounts where parental consent cannot be verified.",
    ],
  },
  {
    title: "3. Parental Consent & Child Protection",
    content: [
      "Protecting minors is our priority.",
      "For Minor Users: Registration requires verifiable parental or guardian consent.",
      "Parental Rights:\n• Request access to or deletion of minor’s personal data.\n• Withdraw consent at any time (results in account termination).\n• View performance and activity metrics.",
      "Prep AI Commitments:\n• No data collection from minors without consent.\n• No behavioral advertising targeting minors.\n• Enhanced safeguards applied to minor accounts.",
      "Any misuse involving minors will be investigated and may be reported to relevant authorities.",
    ],
  },
  {
    title: "4. Account Registration & Security",
    content: [
      "You agree to:\n• Provide accurate and complete information.\n• Keep your login credentials confidential.\n• Notify us immediately of unauthorized access.",
      "Suspension or Termination Grounds:\n• False identity or fraudulent NIN/identification usage.\n• Misuse of AI tools.\n• Violation of these Terms.\n• Academic dishonesty (e.g., cheating during live exams).",
    ],
  },
  {
    title: "5. How the Platform Works",
    content: [
      "Prep AI provides:",
      "• AI-powered tutoring & step-by-step explanations.\n• WAEC, JAMB, and NECO past questions database.\n• Personalized study recommendations.\n• Performance tracking & adaptive assessments.\n• Downloadable study resources.",
      "AI-generated responses are automated and may occasionally contain inaccuracies. Users are encouraged to independently verify important academic content.",
    ],
  },
  {
    title: "6. Acceptable Use",
    content: [
      "You must NOT:",
      "• Use the Platform for unlawful purposes.\n• Attempt to hack, reverse-engineer, or disrupt the AI systems.\n• Upload harmful software or malicious content.\n• Share your account with others.\n• Reproduce or redistribute proprietary exam databases.\n• Use the Platform to facilitate cheating during live exams.",
      "Violation may result in immediate suspension and legal action.",
    ],
  },
  {
    title: "7. Intellectual Property",
    content: [
      "All intellectual property in the Platform – including software, AI systems, branding, and proprietary content — belongs to Publica AI or its licensors.",
      "Limited License: You are granted a limited, non-transferable license to use the Platform for personal educational purposes only.",
      "You may not:\n• Copy or commercially exploit content.\n• Resell access.\n• Create derivative works without express written consent/authorization.",
    ],
  },
  {
    title: "8. User Content",
    content: [
      "If you upload materials (e.g., syllabi or study notes):",
      "• You have granted us automatic right to retain ownership of your content/materials.\n• You grant us express license to process it to deliver services and improve personalization.",
    ],
  },
  {
    title: "9. Data Protection & Privacy",
    content: [
      "We process personal data in accordance with:",
      "• The Nigeria Data Protection Act (NDPA)\n• Applicable child protection laws",
      "Data Collected may include:\n• NIN / Identification\n• Academic preferences\n• Performance analytics\n• Usage data",
      "Minor data is subject to enhanced protection safeguards. Please review our Privacy Policy for full details.",
    ],
  },
  {
    title: "10. Fees & Subscriptions",
    content: [
      "• Some features may require payment.\n• Fees are clearly displayed prior to purchase.\n• Payments are non-refundable.\n• Prices may change with notice.",
      "Parents/Guardians are financially responsible for subscriptions purchased by minors under their supervision.",
    ],
  },
  {
    title: "11. Disclaimer",
    content: [
      "The Platform is provided on an “as is” and “as available” basis.",
      "We do not guarantee:\n• Uninterrupted access\n• Error-free AI responses\n• Specific exam results",
      "Academic performance depends on multiple factors beyond our control.",
    ],
  },
  {
    title: "12. Limitation of Liability",
    content: [
      "To the fullest extent permitted by law, Publica AI shall not be liable for:",
      "• Indirect or consequential damages\n• Academic failure or examination performance\n• Reliance solely on AI-generated outputs\n• Technical interruptions",
    ],
  },
  {
    title: "13. Suspension & Termination",
    content: [
      "We may suspend or terminate accounts where:",
      "• These Terms are breached.\n• Fraud or misuse is suspected.\n• Parental consent is withdrawn.\n• Required identification cannot be verified.",
      "Users may request account deletion at any time.",
    ],
  },
  {
    title: "14. Force Majeure",
    content: [
      "We are not liable for delays or failures caused by events beyond our reasonable control, including:",
      "• Regulatory changes\n• Internet outages\n• Cyberattacks\n• Power disruptions",
    ],
  },
  {
    title: "15. Changes to These Terms",
    content: [
      "We may update these Terms from time to time. Continued use of the Platform after updates constitutes acceptance of the revised Terms.",
    ],
  },
  {
    title: "16. Governing Law",
    content: ["These Terms are governed by the laws of the Federal Republic of Nigeria."],
  },
  {
    title: "17. Contact Us",
    content: [
      "Address: No 8 Montgomery, Yaba, Lagos, Nigeria",
      "Email: support@prepai.education",
      "Phone: +2349115430001",
    ],
  },
];

export function TermsOfServiceScreen() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="app-column">
      <div className="flex-1 overflow-y-auto px-5 pb-10 pt-4">
        <BackButton to="/profile" />

        <h1 className="m-0 pt-5 font-[family-name:var(--font-display)] text-[22px] font-bold leading-[1.25]">
          Terms of Service
        </h1>
        <p className="m-0 pt-1 text-[13px] text-text-muted">Effective: 24th February 2026</p>

        <div className="mt-6 flex flex-col gap-3">
          {sections.map((section, i) => {
            const open = openIndex === i;
            return (
              <div
                key={section.title}
                className="overflow-hidden rounded-[var(--radius-card)] border border-border bg-surface"
              >
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
                >
                  <span className="text-[14px] font-semibold leading-[1.35]">{section.title}</span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    className={`shrink-0 text-text-muted transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                  >
                    <path
                      d="M6 9l6 6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {open ? (
                  <div className="border-t border-border px-4 pb-4 pt-3">
                    {section.content.map((paragraph, j) => (
                      <p
                        key={j}
                        className="m-0 whitespace-pre-line pt-2 text-[13px] leading-[1.6] text-text-muted first:pt-0"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <footer className="mt-8 text-center">
          <p className="m-0 text-[12px] text-text-muted">
            &copy; {new Date().getFullYear()} Publica AI Limited. RC. 7884242.
          </p>
          <p className="m-0 pt-1 text-[12px] text-text-muted">
            No 8 Montgomery, Yaba, Lagos, Nigeria
          </p>
          <p className="m-0 pt-1 text-[12px] text-text-muted">
            support@prepai.education &middot; +2349115430001
          </p>
        </footer>
      </div>
    </div>
  );
}
